import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { PageHeader, TrackIcon } from "@/components/Bits";

const BLANK = { name: "", description: "", discovery_mode: "geography_audit", map_mode: "optional", reporting_label: "", color: "#B65A2B", icon: "Layers",
  qualify_threshold: 40, niches: [{ key: "general", label: "General", source: "osm", osm: ["office=company"], plural: "clients" }], rules: [], templates: {}, followup: {}, active: true };

function Field({ label, children }) {
  return <label className="block space-y-1.5"><span className="kh-label">{label}</span>{children}</label>;
}

function TrackEditor({ track, signals, onClose, onSaved }) {
  const [t, setT] = useState(() => JSON.parse(JSON.stringify(track)));
  const set = (p) => setT((x) => ({ ...x, ...p }));
  const isNew = !track.id;
  const ruleOn = (k) => t.rules.find((r) => r.key === k);
  const toggleRule = (s, on) => set({ rules: on ? [...t.rules, { key: s.key, label: s.label, weight: 10, enabled: true }] : t.rules.filter((r) => r.key !== s.key) });
  const setWeight = (k, w) => set({ rules: t.rules.map((r) => (r.key === k ? { ...r, weight: Number(w) || 0 } : r)) });
  const setNiche = (i, p) => set({ niches: t.niches.map((n, j) => (j === i ? { ...n, ...p } : n)) });
  const save = async () => {
    try {
      const { data } = isNew ? await api.post("/tracks", t) : await api.put(`/tracks/${t.id}`, t);
      toast.success("Track saved"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="track-editor">
        <DialogHeader><DialogTitle className="font-display text-xl">{isNew ? "New track" : `Edit ${track.name}`}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Track name"><Input value={t.name} onChange={(e) => set({ name: e.target.value })} data-testid="track-name-input" /></Field>
          <Field label="Reporting label"><Input value={t.reporting_label} onChange={(e) => set({ reporting_label: e.target.value })} /></Field>
          <div className="sm:col-span-2"><Field label="Description"><Textarea rows={2} value={t.description} onChange={(e) => set({ description: e.target.value })} /></Field></div>
          <Field label="Discovery mode">
            <Select value={t.discovery_mode} onValueChange={(v) => set({ discovery_mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["geography_audit", "source_growth", "visual_brand"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Map / radius">
            <Select value={t.map_mode} onValueChange={(v) => set({ map_mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["primary", "optional", "none"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Qualify threshold (score)"><Input type="number" value={t.qualify_threshold} onChange={(e) => set({ qualify_threshold: Number(e.target.value) })} data-testid="track-threshold-input" /></Field>
          <Field label="Colour"><Input value={t.color} onChange={(e) => set({ color: e.target.value })} /></Field>
          <div className="flex items-center gap-3"><Switch checked={t.active !== false} onCheckedChange={(v) => set({ active: v })} data-testid="track-active-switch" /><span className="text-sm">{t.active !== false ? "Active" : "Archived (hidden from search)"}</span></div>
        </div>
        <div className="mt-4">
          <div className="kh-label mb-2">Niches (directory tags like amenity=dentist, comma separated)</div>
          <div className="space-y-2">
            {t.niches.map((n, i) => (
              <div key={i} className="grid grid-cols-[1fr_110px_1.4fr_auto] gap-2">
                <Input value={n.label} onChange={(e) => setNiche(i, { label: e.target.value, key: n.key || e.target.value.toLowerCase().replace(/\W+/g, "_") })} placeholder="Label" />
                <Select value={n.source} onValueChange={(v) => setNiche(i, { source: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="osm">Directory</SelectItem><SelectItem value="youtube">YouTube</SelectItem></SelectContent>
                </Select>
                <Input value={n.source === "youtube" ? n.query || "" : (n.osm || []).join(", ")} placeholder={n.source === "youtube" ? "search query" : "amenity=dentist"}
                  onChange={(e) => setNiche(i, n.source === "youtube" ? { query: e.target.value } : { osm: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
                <Button variant="ghost" size="sm" onClick={() => set({ niches: t.niches.filter((_, j) => j !== i) })}>×</Button>
              </div>
            ))}
            <Button size="sm" variant="secondary" onClick={() => set({ niches: [...t.niches, { key: `niche_${t.niches.length + 1}`, label: "", source: "osm", osm: [], plural: "clients" }] })} data-testid="track-add-niche-btn">Add niche</Button>
          </div>
        </div>
        {!isNew && (
          <>
            <div className="mt-4">
              <div className="kh-label mb-2">Qualification rules (signal · weight)</div>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {signals.map((s) => {
                  const r = ruleOn(s.key);
                  return (
                    <div key={s.key} className="flex items-center gap-2 rounded-lg border px-3 py-1.5">
                      <Switch checked={!!r} onCheckedChange={(on) => toggleRule(s, on)} data-testid={`rule-toggle-${s.key}`} />
                      <span className="flex-1 text-sm">{s.label}</span>
                      {r && <Input className="h-7 w-14 font-mono text-xs" value={r.weight} onChange={(e) => setWeight(s.key, e.target.value)} />}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <div className="kh-label">Templates — variables: {"{company} {greeting_name} {website} {issue_1} {issue_2} {niche_label} {niche_plural} {city} {sender_name}"}</div>
              {["initial_subject", "initial_body", "followup_subject", "followup_body"].map((k) => k.endsWith("subject")
                ? <Input key={k} value={t.templates[k] || ""} onChange={(e) => set({ templates: { ...t.templates, [k]: e.target.value } })} data-testid={`template-${k}`} />
                : <Textarea key={k} rows={6} value={t.templates[k] || ""} onChange={(e) => set({ templates: { ...t.templates, [k]: e.target.value } })} data-testid={`template-${k}`} />)}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["interval_days", "Follow-up every (days)"], ["max_followups", "Max follow-ups"], ["dead_after_days", "Dead after (days)"], ["reeligible_after_days", "Re-eligible after (days)"]].map(([k, l]) => (
                <Field key={k} label={l}><Input type="number" value={t.followup[k] ?? ""} onChange={(e) => set({ followup: { ...t.followup, [k]: Number(e.target.value) } })} /></Field>
              ))}
            </div>
          </>
        )}
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!t.name} data-testid="track-save-btn">Save track</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Tracks() {
  const [tracks, setTracks] = useState([]);
  const [signals, setSignals] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/tracks").then((r) => setTracks(r.data));
  useEffect(() => { load(); api.get("/signals").then((r) => setSignals(r.data)); }, []);
  return (
    <>
      <PageHeader eyebrow="Configurable service tracks" title="Tracks">
        <Button onClick={() => setEdit(BLANK)} data-testid="new-track-btn"><Plus className="mr-1 h-4 w-4" />New track</Button>
      </PageHeader>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {tracks.map((t, i) => (
          <div key={t.id} className="kh-card kh-rise relative overflow-hidden p-6" style={{ animationDelay: `${i * 60}ms` }} data-testid={`track-card-${t.slug}`}>
            <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: t.color }} />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><TrackIcon name={t.icon} color={t.color} className="h-5 w-5" /><h3 className="font-display text-xl font-medium">{t.name}</h3></div>
              <Button size="sm" variant="ghost" onClick={() => setEdit(t)} data-testid={`edit-track-${t.slug}`}><Pencil className="h-4 w-4" /></Button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{t.description}</p>
            <div className="mt-4 flex flex-wrap gap-1.5">{t.niches.map((n) => <span key={n.key} className="rounded-full border px-2.5 py-0.5 text-xs">{n.label}</span>)}</div>
            <div className="mt-5 grid grid-cols-3 gap-2 border-t pt-4 text-xs">
              <div><div className="text-muted-foreground">Mode</div><div className="font-mono">{t.discovery_mode}</div></div>
              <div><div className="text-muted-foreground">Map</div><div className="font-mono">{t.map_mode}</div></div>
              <div><div className="text-muted-foreground">Threshold</div><div className="font-mono">{t.qualify_threshold}</div></div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">{t.rules.filter((r) => r.enabled).length} rules · follow-up every {t.followup?.interval_days}d · dead after {t.followup?.dead_after_days}d</div>
          </div>
        ))}
      </div>
      {edit && <TrackEditor track={edit} signals={signals} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </>
  );
}
